import React, { useState } from 'react';
import {Button} from '@/components/ui/button';import PasswordInput from '@/components/PasswordInput';
import {Input} from '@/components/ui/input';import {Mail, Lock, KeyRound, ArrowRight, ArrowLeft, Sun, Moon} from 'lucide-react';import {toast} from 'sonner';import axios from 'axios';
import {useNavigate} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {useTheme} from '@/contexts/ThemeContext';import {API_URL as API} from '@/utils/apiConfig';


export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colorMode, changeColorMode } = useTheme();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignupHint, setShowSignupHint] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep('code');
      toast.success(t('forgot.code_sent', 'Reset code sent! Check your email.'));
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.detail || 'Something went wrong';
      if (status === 404) {
        toast.error(typeof msg === 'string' ? msg : t('forgot.no_account', 'No account found with that email.'));
        setShowSignupHint(true);
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t('forgot.passwords_mismatch', 'Passwords do not match'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      toast.success(t('forgot.reset_success', 'Password reset successfully!'));
      navigate('/login');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: 1, label: t('password.weak', 'Weak'), color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: t('password.fair', 'Fair'), color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: t('password.good', 'Good'), color: 'bg-blue-500' };
    return { level: 4, label: t('password.strong', 'Strong'), color: 'bg-green-500' };
  };

  const strength = getPasswordStrength(newPassword);

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 py-10" style={{ minHeight: '100dvh', paddingTop: '2.5rem', background: 'var(--theme-background)' }}>
      {/* Mode toggle */}
      <div className="fixed top-4 right-4 z-50" style={{ top: 'max(1rem, var(--safe-area-top, env(safe-area-inset-top, 0px)))' }}>
        <button
          onClick={() => changeColorMode(colorMode === 'dark' ? 'light' : 'dark')}
          className="p-2 rounded-full transition-all"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          data-testid="forgot-mode-toggle"
        >
          {colorMode === 'dark'
            ? <Sun className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            : <Moon className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />}
        </button>
      </div>
      <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', boxShadow: '0 32px 64px rgba(0,0,0,0.15)', fontFamily: 'var(--theme-font-family)' }}>
        {/* Logo + title */}
        <div className="text-center pt-10 pb-6 px-8">
          <button onClick={() => navigate('/app')} className="inline-block mb-5 hover:scale-105 transition-transform">
            <img src="/logo.png" alt={t('generated.forgotpasswordpage_eili_7ed1')} className="h-20 w-20 mx-auto drop-shadow-lg" />
          </button>
          <h1 className="text-3xl font-bold mb-1" style={{ color: 'var(--theme-text)', fontFamily: "'Playwrite ES Deco', cursive" }}>
            {t('app.title')}
          </h1>
          <h2 className="text-base font-medium mb-1.5" style={{ color: 'var(--theme-text)', fontFamily: 'var(--theme-font-family)' }}>
            {t('forgot.title', 'Reset Password')}
          </h2>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)', fontFamily: 'var(--theme-font-family)' }}>
            {step === 'email'
              ? t('forgot.enter_email', "Enter your email and we'll send you a reset code.")
              : t('forgot.enter_code', 'Enter the code and your new password.')}
          </p>
        </div>

        {/* Form */}
        <div className="px-8 pb-10">
          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                <Input
                  type="email"
                  placeholder={t('login.email_placeholder', 'Email address')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-12 py-6 rounded-xl text-sm"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2 text-white transition-all hover:opacity-90"
                style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
                data-testid="forgot-submit-email"
              >
                {t('forgot.send_code', 'Send Reset Code')}
                <ArrowRight className="h-4 w-4" />
              </Button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm transition-colors"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="forgot-back-to-login"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('forgot.back_to_login', 'Back to Sign In')}
              </button>

              {showSignupHint && (
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/signup')}
                    className="text-sm transition-colors"
                style={{ color: 'var(--theme-textSecondary)' }}
                    data-testid="forgot-signup-link"
                  >
                    {t('forgot.no_account_signup', "Don't have an account?")} <span className="underline">{t('login.signup', 'Sign up')}</span>
                  </button>
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                <Input
                  type="text"
                  placeholder={t('forgot.code_placeholder', '6-digit code')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  maxLength={6}
                  className="pl-12 py-6 rounded-xl text-sm tracking-widest text-center font-mono"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="forgot-code-input"
                />
              </div>

              <div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" style={{ color: 'var(--theme-textSecondary)' }} />
                  <PasswordInput
                    InputComponent={Input}
                    placeholder={t('forgot.new_password', 'New password')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="pl-12 py-6 rounded-xl text-sm"
                    style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                    data-testid="forgot-new-password-input"
                  />
                </div>
                {newPassword && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-bg-active)' }}>
                      <div
                        className={`h-full rounded-full transition-all ${strength.color}`}
                        style={{ width: `${strength.level * 25}%` }}
                      />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{strength.label}</span>
                  </div>
                )}
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" style={{ color: 'var(--theme-textSecondary)' }} />
                <PasswordInput
                  InputComponent={Input}
                  placeholder={t('forgot.confirm_password', 'Confirm new password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="pl-12 py-6 rounded-xl text-sm"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="forgot-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2 text-white transition-all hover:opacity-90"
                style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
                data-testid="forgot-reset-submit"
              >
                {t('forgot.reset_button', 'Reset Password')}
                <ArrowRight className="h-4 w-4" />
              </Button>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); }}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm transition-colors"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('forgot.try_different_email', 'Try a different email')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
